import { UINodeConfig, UIStyle, LayoutRect, DEFAULT_STYLE, resolvePadding, resolveMargin, buildFont } from './types';

interface Size {
  width: number;
  height: number;
}

function resolveStyle(style?: UIStyle): UIStyle {
  return { ...DEFAULT_STYLE, ...(style ?? {}) };
}

export function measure(node: UINodeConfig, ctx: CanvasRenderingContext2D): Map<UINodeConfig, Size> {
  const sizes = new Map<UINodeConfig, Size>();

  const measureNode = (current: UINodeConfig): Size => {
    const style = resolveStyle(current.style);
    const [padTop, padRight, padBottom, padLeft] = resolvePadding(style.padding);
    const [marTop, marRight, marBottom, marLeft] = resolveMargin(style.margin);

    let width = 0;
    let height = 0;

    if (current.triangle) {
      width = current.triangle.width;
      height = current.triangle.height;
    } else if (current.circle) {
      width = current.circle.radius * 2;
      height = current.circle.radius * 2;
    } else if (current.text && (!current.children || current.children.length === 0)) {
      ctx.font = buildFont(style);
      const metrics = ctx.measureText(current.text);
      width = metrics.width;
      height = (style.fontSize ?? DEFAULT_STYLE.fontSize) * 1.25;
    } else if (current.children && current.children.length > 0) {
      const direction = style.direction ?? DEFAULT_STYLE.direction;
      const gap = style.gap ?? DEFAULT_STYLE.gap;

      let mainSize = 0;
      let crossSize = 0;

      for (const child of current.children) {
        const childSize = measureNode(child);
        if (direction === 'row') {
          mainSize += childSize.width;
          crossSize = Math.max(crossSize, childSize.height);
        } else {
          mainSize += childSize.height;
          crossSize = Math.max(crossSize, childSize.width);
        }
      }

      if (current.children.length > 1) {
        mainSize += gap * (current.children.length - 1);
      }

      if (direction === 'row') {
        width = mainSize;
        height = crossSize;
      } else {
        width = crossSize;
        height = mainSize;
      }
    }

    width += padLeft + padRight;
    height += padTop + padBottom;

    // Margin adds to outer size (pushes siblings away)
    width += marLeft + marRight;
    height += marTop + marBottom;

    if (style.width && style.width !== 'auto') {
      width = style.width;
    }
    if (style.height && style.height !== 'auto') {
      height = style.height;
    }

    if (style.minWidth != null) {
      width = Math.max(width, style.minWidth);
    }
    if (style.minHeight != null) {
      height = Math.max(height, style.minHeight);
    }
    if (style.maxWidth != null) {
      width = Math.min(width, style.maxWidth);
    }

    const size = { width, height };
    sizes.set(current, size);
    return size;
  };

  measureNode(node);
  return sizes;
}

export function layout(
  node: UINodeConfig,
  sizes: Map<UINodeConfig, Size>,
  bounds: LayoutRect,
): Map<UINodeConfig, LayoutRect> {
  const rects = new Map<UINodeConfig, LayoutRect>();

  const layoutNode = (current: UINodeConfig, rect: LayoutRect): void => {
    rects.set(current, rect);

    if (!current.children || current.children.length === 0) {
      return;
    }

    const style = resolveStyle(current.style);
    const direction = style.direction ?? DEFAULT_STYLE.direction;
    const gap = style.gap ?? DEFAULT_STYLE.gap;
    const [padTop, padRight, padBottom, padLeft] = resolvePadding(style.padding);

    const contentX = rect.x + padLeft;
    const contentY = rect.y + padTop;
    const contentWidth = rect.width - padLeft - padRight;
    const contentHeight = rect.height - padTop - padBottom;

    const childSizes = current.children.map((child) => sizes.get(child)).filter((size): size is Size => !!size);

    let mainSize = 0;
    let crossSize = 0;
    for (const size of childSizes) {
      if (direction === 'row') {
        mainSize += size.width;
        crossSize = Math.max(crossSize, size.height);
      } else {
        mainSize += size.height;
        crossSize = Math.max(crossSize, size.width);
      }
    }

    let actualGap = gap;
    if (current.children.length > 1) {
      mainSize += gap * (current.children.length - 1);
    }

    const contentMain = direction === 'row' ? contentWidth : contentHeight;
    const contentCross = direction === 'row' ? contentHeight : contentWidth;

    let mainOffset = 0;
    switch (style.justifyContent ?? DEFAULT_STYLE.justifyContent) {
      case 'center':
        mainOffset = (contentMain - mainSize) / 2;
        break;
      case 'end':
        mainOffset = contentMain - mainSize;
        break;
      case 'space-between':
        if (current.children.length > 1) {
          actualGap = (contentMain - (mainSize - gap * (current.children.length - 1))) / (current.children.length - 1);
          mainSize = contentMain;
          mainOffset = 0;
        }
        break;
      default:
        mainOffset = 0;
        break;
    }

    const crossAlign = style.alignItems ?? DEFAULT_STYLE.alignItems;

    let cursor = (direction === 'row' ? contentX : contentY) + mainOffset;

    for (let i = 0; i < current.children.length; i++) {
      const child = current.children[i];
      const size = sizes.get(child);
      if (!size) continue;

      const childMainSize = direction === 'row' ? size.width : size.height;
      const childCrossSize = direction === 'row' ? size.height : size.width;

      let crossOffset = 0;
      if (crossAlign === 'center') {
        crossOffset = (contentCross - childCrossSize) / 2;
      } else if (crossAlign === 'end') {
        crossOffset = contentCross - childCrossSize;
      }

      // Margin: offset rect inward, shrink content area
      const childStyle = resolveStyle(child.style);
      const [cMarTop, cMarRight, cMarBottom, cMarLeft] = resolveMargin(childStyle.margin);

      const childRect: LayoutRect = direction === 'row'
        ? {
          x: cursor + cMarLeft,
          y: contentY + crossOffset + cMarTop,
          width: size.width - cMarLeft - cMarRight,
          height: size.height - cMarTop - cMarBottom,
        }
        : {
          x: contentX + crossOffset + cMarLeft,
          y: cursor + cMarTop,
          width: size.width - cMarLeft - cMarRight,
          height: size.height - cMarTop - cMarBottom,
        };

      layoutNode(child, childRect);
      cursor += childMainSize + actualGap;
    }
  };

  layoutNode(node, bounds);
  return rects;
}
